import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import Header from "../Includes/Header";
import Footer from "../Includes/Footer";
import API_BASE_URL from "../apiBase";

const extractId = (slug = "") => {
  // supports formats like "3" or "3-some-title"
  if (!slug) return null;
  const firstPart = slug.split("-")[0];
  const maybeId = parseInt(firstPart, 10);
  return Number.isNaN(maybeId) ? null : maybeId;
};

/**
 * Extracts headings from HTML content and generates unique IDs
 * @param {string} htmlContent - The HTML content to parse
 * @returns {Array} Array of heading objects with {text, level, id}
 */
const extractHeadings = (htmlContent) => {
  if (!htmlContent) return [];
  
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, "text/html");
  const headings = doc.querySelectorAll("h1, h2, h3, h4");
  const tocArray = [];
  const usedIds = new Set();

  headings.forEach((heading) => {
    let id = heading.id;

    // Generate ID if not present
    if (!id) {
      let baseId = heading.textContent
        .toLowerCase()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .trim();

      if (!baseId) {
        baseId = "heading";
      }

      // Ensure unique ID
      let uniqueId = baseId;
      let counter = 1;
      while (usedIds.has(uniqueId)) {
        uniqueId = `${baseId}-${counter}`;
        counter++;
      }

      id = uniqueId;
    }

    usedIds.add(id);

    const level = heading.tagName.toLowerCase(); // h1, h2, h3, h4

    tocArray.push({
      text: heading.textContent.trim(),
      level: level,
      id: id,
    });
  });

  return tocArray;
};

/**
 * Table of Contents Sidebar Component
 */
function TableOfContents({ toc }) {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offsetTop = element.offsetTop - 80; // Account for fixed header
      window.scrollTo({
        top: offsetTop,
        behavior: "smooth",
      });
    }
  };

  if (!toc || toc.length === 0) {
    return null;
  }

  return (
    <nav
      id="toc-sidebar"
      style={{
        position: "absolute",
        right: "20px",
        top: "236px",
        width: "250px",
        maxHeight: "70vh",
        overflowY: "auto",
        backgroundColor: "#f8f9fa",
        border: "1px solid #e9ecef",
        borderRadius: "6px",
        padding: "16px",
        zIndex: 100,
      }}
    >
      <h6
        style={{
          fontSize: "13px",
          fontWeight: "600",
          textTransform: "uppercase",
          color: "#495057",
          marginBottom: "12px",
          marginTop: 0,
        }}
      >
        Table of Contents
      </h6>
      <ul
        style={{
          listStyle: "none",
          padding: 0,
          margin: 0,
        }}
      >
        {toc.map((item, index) => (
          <li key={index} className={`toc-${item.level.toLowerCase()}`}>
            <button
              onClick={() => scrollToSection(item.id)}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "6px 8px",
                marginBottom: "4px",
                border: "none",
                backgroundColor: "transparent",
                color: "#495057",
                textDecoration: "none",
                fontSize: "13px",
                cursor: "pointer",
                borderRadius: "4px",
                transition: "all 0.2s ease",
                marginLeft:
                  item.level === "h1"
                    ? "0"
                    : item.level === "h2"
                      ? "12px"
                      : item.level === "h3"
                        ? "24px"
                        : "36px",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#e9ecef";
                e.target.style.color = "#212529";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "transparent";
                e.target.style.color = "#495057";
              }}
            >
              {item.text}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export default function BlogDetail() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [toc, setToc] = useState([]);

  useEffect(() => {
    const id = extractId(slug);
    if (!id) {
      setError("Invalid blog id");
      setBlog(null);
      return;
    }
    const fetchBlog = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${API_BASE_URL}/admin/get_blog.php?id=${id}`);
        const raw = await res.text();
        let data;
        try {
          data = JSON.parse(raw);
        } catch {
          throw new Error("Received invalid response from server.");
        }
        if (res.ok && data.success) {
          setBlog(data.blog);
          // Extract headings from the blog content
          const headings = extractHeadings(data.blog.description || "");
          setToc(headings);
        } else {
          throw new Error(data.error || `Failed to load blog (${res.status})`);
        }
      } catch (err) {
        setError(err?.message || "Failed to load blog");
        setBlog(null);
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [slug]);

  // Add IDs to headings in the DOM after rendering
  useEffect(() => {
    if (blog && blog.description) {
      // Give React time to render the content
      setTimeout(() => {
        const contentDiv = document.querySelector(".post-content");
        if (contentDiv) {
          const headings = contentDiv.querySelectorAll("h1, h2, h3, h4");
          const usedIds = new Set();

          headings.forEach((heading) => {
            if (!heading.id) {
              let baseId = heading.textContent
                .toLowerCase()
                .replace(/[^\w\s-]/g, "")
                .replace(/\s+/g, "-")
                .replace(/-+/g, "-")
                .trim();

              if (!baseId) {
                baseId = "heading";
              }

              let uniqueId = baseId;
              let counter = 1;
              while (usedIds.has(uniqueId)) {
                uniqueId = `${baseId}-${counter}`;
                counter++;
              }

              heading.id = uniqueId;
            }
            usedIds.add(heading.id);
          });
        }
      }, 0);
    }
  }, [blog]);

  const formattedDate = (value) => {
    if (!value) return "";
    try {
      return new Date(value).toLocaleDateString("en-IN", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return value;
    }
  };

  return (
    <>
      <Header />
      <main id="page-content" className="main-page">
        <div className="container py-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="mb-0">Blog Details</h4>
            <Link to="/blog" className="btn btn-outline-secondary btn-sm">
              ← Back to Blog
            </Link>
          </div>
          {loading && <p>Loading blog...</p>}
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
          {!loading && !error && !blog && (
            <p className="text-muted">No blog found.</p>
          )}
        </div>

        {blog && (
          <>
            <section className="post-thumbnail text-center">
              {blog.image_url ? (
                <img
                  src={blog.image_url}
                  alt={blog.title}
                  width="900"
                  height="600"
                  className="img-fluid d-inline-block"
                  style={{ objectFit: "cover" }}
                />
              ) : null}
            </section>

            <section className="container my-5">
              <header className="post-header mb-3">
                <h1 className="post-title">{blog.title}</h1>
                {blog.created_at && (
                  <p className="text-muted mb-0">
                    {formattedDate(blog.created_at)}
                  </p>
                )}
              </header>

              <article className="post-content">
                <div
                  dangerouslySetInnerHTML={{
                    __html: blog.description || "",
                  }}
                />
              </article>

              {/* Table of Contents Sidebar */}
              <TableOfContents toc={toc} />
            </section>
          </>
        )}
      </main>
      <Footer />
    </>
  );
}